import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { jwtDecode } from "jwt-decode";
import infoIcon from "../assets/icons/Info.svg";
import { GetUsersByDateRangeAsync, UserData } from "../../Admin-Api/GetUsersByDateRangeAsync";
import { getAddressesByUserIdAsync } from "../../Admin-Api/GetAddressesByUserIdAsync.ts";
import AllUsers from "./AllUsers";
import { Search } from "lucide-react";

interface CustomJwtPayload {
  RoleId: string;
}

const ManageUser: React.FC = () => {
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [userData, setUserData] = useState<UserData[]>([]);
  const [displayedUserData, setDisplayedUserData] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchData, setSearchData] = useState("");
  const [suggestions, setSuggestions] = useState<UserData[]>([]);
  const [activeTab, setActiveTab] = useState<"manage" | "all">("all");

  const handleUserClick = async (user: UserData) => {

    sessionStorage.setItem(
      "manageUserState",
      JSON.stringify({
        startDate,
        endDate,
        userData,
        displayedUserData,
        searchData,
        activeTab: "manage",
      })
    );

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      return;
    }

    try {
      const addresses = await getAddressesByUserIdAsync(user.id.toString(), token);
      if (addresses.length > 0) {
        navigate(`/user-details/${user.id}`, {
          state: {
            userName: user.userName,
            email: user.email,
          },
        });
      } else {
        toast.info("No address was added by this user.");
      }
    } catch {
      toast.error("Error checking user's addresses.");
    }
  };

  const formatDateString = (dateString: string): string =>
    dateString ? dateString.split("T")[0] : "";


  const handleSuggestionClick = (userId: number) => {
    setSearchData("");
    setSuggestions([]);
    const filtered = userData.filter(user => user.id === userId);
    setDisplayedUserData(filtered);
  };

  useEffect(() => {
    const savedState = sessionStorage.getItem("manageUserState");
    const savedTab = sessionStorage.getItem("activeTab");

    const cameFromDetails = sessionStorage.getItem("fromUserDetails");

    if (savedState && savedTab === "manage" && cameFromDetails === "true") {
      const parsed = JSON.parse(savedState);

      setStartDate(parsed.startDate ? new Date(parsed.startDate) : null);
      setEndDate(parsed.endDate ? new Date(parsed.endDate) : null);
      setUserData(parsed.userData || []);
      setDisplayedUserData(parsed.displayedUserData || []);
      setSearchData(parsed.searchData || "");

      setActiveTab("manage");
    }

    // always clear flag after check
    sessionStorage.removeItem("fromUserDetails");
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      navigate("/");
    } else {
      try {
        const decodedToken = jwtDecode<CustomJwtPayload>(token);
        if (decodedToken.RoleId !== "1") {
          toast.error("Unauthorized access: RoleId is not 1");
          navigate("/");
        }
      } catch (error) {
        toast.error("Invalid token. Please log in again.");
        navigate("/");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!startDate && !endDate) {
      setUserData([]);
      setDisplayedUserData([]);
      setSearchData("");
      setSuggestions([]);
    }
  }, [startDate, endDate]);

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const fetchUserData = async (fromDate: string, toDate: string) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No auth token found");
      }
      const data = await GetUsersByDateRangeAsync(fromDate, toDate, token);
      setUserData(data);
      setDisplayedUserData(data);
      if (data.length === 0) {
        if (!toast.isActive("no-users")) {
          // toast.info("No users were registered in the selected date range.", { toastId: "no-users" });
        }
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchData(e.target.value);
  };

  const applyFilters = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found. Please log in.");
      return;
    }

    let data: UserData[] = [];

    // CASE 1: Date filter exists → fetch from API
    if (startDate && endDate) {
      if (startDate > endDate) {
        toast.error("Start date must be earlier than or equal to end date.");
        return;
      }

      const from = formatDate(startDate);
      const to = formatDate(endDate);

      setIsLoading(true);
      data = await GetUsersByDateRangeAsync(from, to, token);
      setIsLoading(false);
    } else {
      // CASE 2: No date → you should ideally fetch all users (or cached data)
      setIsLoading(true);
      data = await GetUsersByDateRangeAsync("1970-01-01", formatDate(new Date()), token);
      setIsLoading(false);
    }

    // CASE 3: Email filter (optional)
    if (searchData.trim()) {
      data = data.filter(user =>
        user.email.toLowerCase().includes(searchData.toLowerCase())
      );
    }

    setUserData(data);
    setDisplayedUserData(data);

    if (data.length === 0) {
      // toast.info("No users found.");
    }
  };

  const handleTabChange = (tab: "manage" | "all") => {
    setActiveTab(tab);

    sessionStorage.setItem("activeTab", tab);

    if (tab === "all") {
      sessionStorage.removeItem("manageUserState");

      setStartDate(null);
      setEndDate(null);
      setUserData([]);
      setDisplayedUserData([]);
      setSearchData("");
      setSuggestions([]);
    }
  };

  return (
    <div className="h-screen overflow-auto bg-[#f5f7fb] select-none">
      <div className="flex flex-col min-h-screen">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-8 py-4 bg-white shadow-sm">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <span className="bg-blue-600 w-2 h-8 rounded mr-3"></span>
            Manage User
          </h1>

          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg w-fit">
            <button
              type="button"
              onClick={() => handleTabChange("all")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === "all"
                  ? "bg-white shadow text-blue-600"
                  : "text-gray-600 hover:text-gray-800"
                }`}
            >
              All Users
            </button>

            <button
              type="button"
              onClick={() => handleTabChange("manage")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === "manage"
                  ? "bg-white shadow text-blue-600"
                  : "text-gray-600 hover:text-gray-800"
                }`}
            >
              Manage Users
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto px-6 py-5">
          <div className="max-w-7xl mx-auto">
            {activeTab === "all" && <AllUsers />}

            {activeTab === "manage" && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 px-5 py-4 border-b border-gray-200">
                  <div>
                    <div className="flex items-center">
                      <h4 className="text-[18px] font-semibold text-[#202124]">
                        User Management Statistics
                      </h4>

                      {displayedUserData.length > 0 && (
                        <span className="ml-5 text-[14px] text-[#70757A]">
                          {displayedUserData.length} Records
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 mt-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Start Date
                        </label>
                        <DatePicker
                          selected={startDate}
                          onChange={(date) => {
                            setStartDate(date);
                            if (endDate && date && endDate < date) {
                              setEndDate(null);
                            }
                          }}
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          maxDate={new Date()}
                          dateFormat="dd-MM-yyyy"
                          className="h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                          placeholderText="dd-mm-yyyy"
                          isClearable
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          End Date
                        </label>
                        <DatePicker
                          selected={endDate}
                          onChange={(date) => setEndDate(date)}
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          minDate={startDate || undefined}
                          maxDate={new Date()}
                          dateFormat="dd-MM-yyyy"
                          className="h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                          placeholderText="dd-mm-yyyy"
                          disabled={!startDate}
                          isClearable
                        />
                      </div>

                      <button
                        onClick={applyFilters}
                        className="h-10 px-4 mt-5 sm:mt-[21px] bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                      >
                        Fetch Data
                      </button>
                    </div>
                  </div>

                  <div className="relative w-full lg:w-[300px]">
                    <Search
                      size={17}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Search user by email"
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      value={searchData}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          applyFilters();
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="px-5 py-4">
                  {isLoading && (
                    <div className="bg-white/80 rounded-xl p-8 text-center text-gray-500">
                      Loading data...
                    </div>
                  )}

                  {!isLoading && displayedUserData.length === 0 && (
                    <div className="bg-white/80 rounded-xl p-8 text-center text-gray-500">
                      {!startDate && !endDate
                        ? "Select a date range to view users."
                        : "No users found."}
                    </div>
                  )}

                  {!isLoading && displayedUserData.length > 0 && (
                    <>
                      <div className="hidden md:block overflow-x-auto rounded-md">
                        <table className="min-w-full">
                          <thead>
                            <tr className="bg-blue-500 border-b border-gray-200">
                              {[
                                "User Name",
                                "Email",
                                "Registered Date",
                                "Status",
                              ].map((header) => (
                                <th
                                  key={header}
                                  className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide"
                                >
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-200">
                            {displayedUserData.map((user) => (
                              <tr
                                key={user.id}
                                onClick={() => handleUserClick(user)}
                                className="hover:bg-blue-50/50 transition-colors duration-150 cursor-pointer"
                              >
                                <td className="px-4 py-4 text-left text-[14px] text-[#202124]">
                                  {user.userName || "-"}
                                </td>

                                <td className="px-4 py-4 text-left text-[14px] text-[#202124] break-all">
                                  {user.email || "-"}
                                </td>

                                <td className="px-4 py-4 text-left text-[14px] text-[#202124]">
                                  {formatDateString(user.createdDate) || "-"}
                                </td>

                                <td className="px-4 py-4 text-left text-[14px]">
                                  <span
                                    className={`px-3 py-1 rounded-full text-xs font-medium ${user.status
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-700"
                                      }`}
                                  >
                                    {user.status ? "Active" : "Inactive"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="md:hidden space-y-4">
                        {displayedUserData.map((user) => (
                          <div
                            key={user.id}
                            onClick={() => handleUserClick(user)}
                            className="bg-white/90 rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer"
                          >
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div>
                                <h5 className="text-base font-semibold text-gray-800">
                                  {user.userName || "-"}
                                </h5>
                                <p className="text-sm text-gray-500 break-all">
                                  {user.email || "-"}
                                </p>
                              </div>

                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium shrink-0 ${user.status
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                  }`}
                              >
                                {user.status ? "Active" : "Inactive"}
                              </span>
                            </div>

                            <div className="flex justify-between gap-3 text-sm text-gray-700">
                              <span className="text-gray-500">
                                Registered Date
                              </span>
                              <span className="text-right">
                                {formatDateString(user.createdDate) || "-"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <ToastContainer />
    </div>
  );
};

export default ManageUser;
